import React, {useEffect, useState} from 'react';
import {Button, Table, Tag, Typography} from 'antd';
import {FilterOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {
  ReturnNAOnEmpty,
  couponCodesUsersFilter,
  PageSizeArray,
  PaginationPosition,
  formatDate,
  getStatusInfoOfEventRegistration,
} from '../../../Util/commonUtility';
import {useSelector} from 'react-redux';
import useDebouncedCallback from '../../../hooks/useDebounce';
import DynamicSearchForm from '../../CommonComponents/DynamicSearch';
import useLocalization from '../../../Localization';

const {Text, Title} = Typography;

const CouponUsersTrackComponent = (props) => {
  let navigate = useNavigate();
  const {ApplicationString} = useLocalization();
  const {
    allCouponCodeUsers,
    couponMeta,
    paging,
    handleTableChange,
    handlePaginationChange,
    sortParams,
    searchValues,
    handleSearch,
    eventsList,
    setEventsList,
    setFetching,
    fetching,
    eventIdOptions,
  } = props;
  const pageSize = useSelector(({pageSizes}) => pageSizes.pageSizes.coupons);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    searchValues && setExpanded(true);
  }, [searchValues]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const renderAttendeeList = (record, renderItem) => {
    const list = record?.attendees || [];
    if (list.length === 0) return <span>-</span>;

    return (
      <div style={{maxHeight: 140, overflow: 'auto'}}>
        {list.map((attendee, index) => (
          <div key={`${attendee.attendee_email}-${index}`}>
            {renderItem(attendee, index)}
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) =>
        renderAttendeeList(record, (attendee) => {
          const firstName = ReturnNAOnEmpty(attendee.attendee_first_name);
          const lastName = ReturnNAOnEmpty(attendee.attendee_last_name);
          return `${firstName} ${lastName}`.trim();
        }),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) =>
        renderAttendeeList(record, (attendee) =>
          ReturnNAOnEmpty(attendee.attendee_email),
        ),
    },
    {
      title: 'Registrations',
      dataIndex: 'attendee_count',
      key: 'attendee_count',
      sorter: true,
      width: 130,
      render: (count, record) => count ?? record?.attendees?.length ?? 0,
    },
    {
      title: 'Registration Status',
      key: 'status',
      width: 170,
      render: (_, record) =>
        renderAttendeeList(record, (attendee) => {
          const {color, label} = getStatusInfoOfEventRegistration(
            attendee.status,
            ApplicationString,
          );
          return <Tag color={color}>{label}</Tag>;
        }),
    },
    {
      title: 'Event',
      key: 'event',
      render: (_, record) => {
        const list = record?.attendees || [];
        if (list.length === 0) return <span>-</span>;

        const uniqueEvents = [];
        const seen = new Set();
        list.forEach((attendee) => {
          if (!attendee?.event_id || seen.has(attendee.event_id)) return;
          seen.add(attendee.event_id);
          uniqueEvents.push(attendee);
        });

        return (
          <div style={{maxHeight: 140, overflow: 'auto'}}>
            {uniqueEvents.map((event) => {
              const fullName = event.event_name || `Event #${event.event_id}`;
              return (
                <div key={event.event_id} style={{whiteSpace: 'nowrap'}}>
                  [{event.event_id}] {fullName}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      width: 180,
      render: (text) => (text ? formatDate(text) : '-'),
    },
  ];

  columns.forEach((column) => {
    if (column.key !== 'action') {
      column.sortOrder =
        sortParams.field === column.dataIndex && sortParams.order;
    }
  });

  const debouncedHandlePaginationChange = useDebouncedCallback(
    handlePaginationChange,
    1000,
  );
  const debouncedTableChange = useDebouncedCallback(handleTableChange, 1000);

  const couponType = couponMeta?.coupon_type;
  const isCart = couponMeta?.apply_to_whole_cart || couponType === 'Cart';

  return (
    <div className='main-container'>
      <div className='search-container' style={{gap: '10px'}}>
        <Button
          onClick={toggleExpand}
          id='CouponUsersTrackComponent-button-filterSearch'>
          <FilterOutlined /> {ApplicationString['dynamicSearch.button.heading']}
        </Button>
        <Button
          onClick={() => navigate('/admin/couponCodes')}
          id='EventTemplateSchedulerComponent-button-back'>
          Back To Coupon Codes
        </Button>
      </div>
      {expanded && (
        <DynamicSearchForm
          columns={couponCodesUsersFilter}
          onSearch={handleSearch}
          savedFilteredValues={searchValues}
          title='couponUsers'
          eventsList={eventsList}
          setFetching={setFetching}
          fetching={fetching}
          setEventsList={setEventsList}
          eventIdOptions={eventIdOptions}
        />
      )}

      <div
        className='listing-container'
        style={{
          marginBottom: 16,
          padding: '16px 20px',
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          borderRadius: 8,
        }}>
        <Title level={5} style={{margin: '0 0 12px'}}>
          Coupon Usage Summary
        </Title>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 24px',
            alignItems: 'center',
          }}>
          <div>
            <Text type='secondary'>Coupon Code </Text>
            <Text strong code>
              {ReturnNAOnEmpty(couponMeta?.coupon_code)}
            </Text>
          </div>
          <div>
            <Text type='secondary'>Type </Text>
            <Tag color={isCart ? 'blue' : 'green'}>
              {isCart ? 'Cart' : 'Individual'}
            </Tag>
          </div>
          {couponMeta?.event_name && (
            <div>
              <Text type='secondary'>Linked Event </Text>
              <Text>
                [{couponMeta.event_id}] {couponMeta.event_name}
              </Text>
            </div>
          )}
          <div>
            <Text type='secondary'>Total Quantity </Text>
            <Text strong>{couponMeta?.qty ?? 0}</Text>
          </div>
          <div>
            <Text type='secondary'>Used Quantity </Text>
            <Text strong>{couponMeta?.used_qty ?? 0}</Text>
          </div>
          <div>
            <Text type='secondary'>Total Registrations </Text>
            <Text strong>{couponMeta?.total_registrations ?? 0}</Text>
          </div>
          {isCart && (
            <div>
              <Text type='secondary'>Total Checkouts </Text>
              <Text strong>{couponMeta?.total_checkouts ?? 0}</Text>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px 12px',
            marginTop: 12,
            alignItems: 'center',
          }}>
          <Text type='secondary'>Registration Status Breakdown</Text>
          {(couponMeta?.registration_status_stats || [])
            .filter((stat) => (stat.registration_count ?? 0) > 0)
            .map((stat) => {
            const {color, label} = getStatusInfoOfEventRegistration(
              stat.status,
              ApplicationString,
            );
            return (
              <Tag key={stat.status} color={color}>
                {label}: {stat.registration_count ?? 0}
              </Tag>
            );
          })}
        </div>
      </div>

      <div className='listing-container'>
        <Table
          columns={columns}
          dataSource={allCouponCodeUsers}
          pagination={{
            pageSize: pageSize,
            total: paging?.totalCount ?? paging?.total,
            current: paging?.page ?? paging?.current,
            onChange: debouncedHandlePaginationChange,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) =>
              isCart ? `Total ${total} checkouts` : `Total ${total} items`,
            position: [PaginationPosition],
          }}
          onChange={debouncedTableChange}
          rowKey='group_key'
          scroll={{x: true}}
        />
      </div>
    </div>
  );
};

export default CouponUsersTrackComponent;
